import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeetingSamenvatting, OpdrachtRij, Seniority, Serverfase } from '@vip/shared';
import { api } from '../../lib/apiClient';
import { IS_BEZIG, weergave } from './gesprekstand';

/**
 * Hoe vaak een scherm vraagt terwijl de server bezig is. Het eerste uur snel,
 * daarna eens per minuut: wat dan nog loopt maakt de bewaker op de server
 * binnen een dag af of geeft hij op. Stoppen deed het scherm vroeger, en dan
 * zag een buddy nooit dat het alsnog lukte.
 */
function useTempo(): (snel: number) => number {
  const start = useRef(Date.now());
  return (snel) => (Date.now() - start.current < 60 * 60_000 ? snel : 60_000);
}

export const gesprekKeys = {
  alle: ['gesprekken'] as const,
  detail: (id: string) => ['gesprekken', id] as const,
  transcript: (id: string) => ['gesprekken', id, 'transcript'] as const,
  keywords: (id: string) => ['gesprekken', id, 'keywords'] as const,
  voortgang: (id: string) => ['voortgang', id] as const,
};

export interface Deelnemer {
  id: string;
  displayName: string;
  vorigeSeniority: Seniority | null;
  /** `null` zonder buddy, anders diens id. Bepaalt of het roster de opdracht
   *  mag laten wijzigen of alleen laat zien wat er al staat. */
  buddyId: string | null;
  opdrachten: OpdrachtRij[];
}

export function useDeelnemers() {
  return useQuery({
    queryKey: ['deelnemers'],
    queryFn: () =>
      api.get<{ participants: Deelnemer[] }>('/participants').then((r) => r.participants),
  });
}

export function useGesprekken() {
  const tempo = useTempo();
  return useQuery({
    queryKey: gesprekKeys.alle,
    queryFn: () =>
      api.get<{ meetings: MeetingSamenvatting[] }>('/meetings').then((r) => r.meetings),
    // Een upload zonder opname lost zich niet op; daar wordt niet op gevraagd.
    refetchInterval: (q) =>
      q.state.data?.some((m) => IS_BEZIG.includes(weergave(m))) ? tempo(5000) : false,
  });
}

export function useGesprek(id: string) {
  const tempo = useTempo();
  return useQuery({
    queryKey: gesprekKeys.detail(id),
    queryFn: () => api.get<{ meeting: MeetingDetail; roster: RosterRegel[] }>(`/meetings/${id}`),
    refetchInterval: (q) => {
      const m = q.state.data?.meeting;
      const bezig = m && IS_BEZIG.includes(weergave({ ...m, heeftOpname: m.audioR2Key !== null }));
      return bezig ? tempo(4000) : false;
    },
  });
}

export interface MeetingDetail {
  id: string;
  occurredAt: string;
  status: MeetingSamenvatting['status'];
  failureReason: string | null;
  audioR2Key: string | null;
}

/** Zoals hij terugkomt van de server. `isBuddy` is er alleen nog voor
 *  gesprekken van vóór het moment dat de buddy uit de sessie komt; nieuwe
 *  gesprekken hebben hem overal op false. */
export interface RosterRegel {
  participantId: string | null;
  displayName: string | null;
  isBuddy: boolean;
  seniority: Seniority;
}

export function useMaakGesprek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoer: {
      occurredAt: string;
      titel?: string;
      roster: Array<{ participantId: string | null; seniority: Seniority }>;
    }) => api.post<{ id: string }>('/meetings', invoer),
    onSuccess: () => qc.invalidateQueries({ queryKey: gesprekKeys.alle }),
  });
}

export function useHernoemGesprek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, titel }: { id: string; titel: string }) =>
      api.patch(`/meetings/${id}`, { titel }),
    onSuccess: () => qc.invalidateQueries({ queryKey: gesprekKeys.alle }),
  });
}

export function useVerwijderGesprek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/meetings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gesprekKeys.alle }),
  });
}

// --- scherm 1: transcript ---------------------------------------------------

export interface TranscriptReview {
  transcript: {
    id: string;
    /** Wat de buddy controleert. `null` als het samenvatten is misgelopen.
     *  Het ruwe transcript blijft op de server en komt hier niet langs. */
    samenvatting: string | null;
    language: string | null;
    durationSeconds: number | null;
    confirmedAt: string | null;
  };
}

/**
 * Het transcript en de samenvatting.
 *
 * Blijft hervragen zolang de verwerking loopt: die draait op de achtergrond en
 * zonder dit moest de buddy zelf verversen om te merken dat hij klaar was.
 * `retry: false` omdat een 404 hier "nog niet klaar" betekent en geen fout; de
 * herhaling doet het wachten, niet de retry.
 *
 * Op de status en niet op "is er al data". Dat laatste stond er, en daar zat de
 * fout: de transcriptrij bestaat al zodra de uitgeschreven tekst is opgeslagen,
 * terwijl daarna nog een halve minuut wordt samengevat. Die eerste uitvraag kwam
 * dus terug met `samenvatting: null`, waarna de herhaling stopte omdat er data
 * was. De samenvatting die daarna werd weggeschreven werd nooit meer opgehaald,
 * dus stond er "geen samenvatting" tot iemand het scherm ververste.
 *
 * En dan de omslag zelf. Die komt bijna altijd nét ná de laatste uitvraag,
 * precies zoals bij de hulpvragen: die kwam nog zonder samenvatting terug,
 * waarna de herhaling stopte omdat er niets meer liep. Vandaar één gerichte
 * uitvraag op het moment dat de verwerking klaar is.
 */
export function useTranscript(id: string, status?: string) {
  const qc = useQueryClient();
  const tempo = useTempo();
  const bezig = status === 'UPLOADED' || status === 'TRANSCRIBING';

  useEffect(() => {
    if (status === 'TRANSCRIBED') qc.invalidateQueries({ queryKey: gesprekKeys.transcript(id) });
  }, [status, id, qc]);

  return useQuery({
    queryKey: gesprekKeys.transcript(id),
    queryFn: () => api.get<TranscriptReview>(`/meetings/${id}/transcript`),
    retry: false,
    refetchInterval: (q) => (bezig && !q.state.data?.transcript.samenvatting ? tempo(3000) : false),
  });
}

export interface Voortgang {
  status: string;
  voortgang: {
    tekens: number;
    tekst: string;
    /** Welke stap er nu loopt. De lijst staat in shared, zodat de server en dit
     *  scherm niet uit elkaar kunnen lopen. */
    fase?: Serverfase;
    /** Wanneer deze stap begon, als ISO-string. Het scherm telt daarvandaan op. */
    sinds?: string;
  } | null;
}

/** De tekst zoals hij er nú staat, tijdens het transcriberen. */
export function useVoortgang(id: string, actief: boolean) {
  const tempo = useTempo();
  return useQuery({
    queryKey: gesprekKeys.voortgang(id),
    queryFn: () => api.get<Voortgang>(`/meetings/${id}/voortgang`),
    enabled: actief,
    refetchInterval: () => (actief ? tempo(2000) : false),
  });
}

/** De buddy past de tekst zelf aan. Voorstellen waarvan de tekst verdwijnt
 *  vervallen serverzijdig. */
export function useHersamenvat(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ samenvatting: string }>(`/meetings/${id}/transcript/hersamenvat`, {}),
    // Het hersamenvatten schrijft zijn tussenstand naar dezelfde plek als het
    // samenvatten in de keten, dus het scherm laat hem met hetzelfde pollen
    // groeien. Wat daarbij weg moet is de vorige tussenstand: die staat nog in
    // de cache van een eerdere ronde, en react-query geeft hem terug op het
    // moment dat het pollen weer aangaat. Zonder dit stond de samenvatting van
    // de vorige poging er een tik voordat de nieuwe begon.
    onMutate: () => qc.removeQueries({ queryKey: gesprekKeys.voortgang(id) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: gesprekKeys.transcript(id) }),
  });
}

export function useBewerkTranscript(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tekst: string) =>
      api.patch<{ vervallen: number }>(`/meetings/${id}/transcript`, { tekst }),
    onSuccess: () => qc.invalidateQueries({ queryKey: gesprekKeys.transcript(id) }),
  });
}

export function useKeurTranscriptGoed(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ toegepast: number }>(`/meetings/${id}/transcript/goedkeuren`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gesprekKeys.transcript(id) });
      qc.invalidateQueries({ queryKey: gesprekKeys.detail(id) });
      qc.invalidateQueries({ queryKey: gesprekKeys.alle });
    },
  });
}

// --- scherm 2: hulpvragen ---------------------------------------------------

export interface HulpvraagRij {
  id: string;
  onderwerpNaam: string;
  deelonderwerpNaam: string;
  vraag: string;
  nieuwOnderwerp: boolean;
  bron: 'llm' | 'buddy';
  confirmedAt: string | null;
}

export interface TaxonomieOnderwerp {
  id: string;
  naam: string;
  deelonderwerpen: Array<{ id: string; naam: string; aantal: number }>;
}

export interface HulpvraagOverzicht {
  hulpvragen: HulpvraagRij[];
  samenvatting: string | null;
  taxonomie: TaxonomieOnderwerp[];
  /** Losse woorden uit de namen van mensen en opdrachtgevers. Het scherm
   *  waarschuwt ermee: een hulpvraag hoort geen namen te bevatten. */
  namen: string[];
}

/**
 * Blijft hervragen zolang het zoeken loopt; zonder dat moest de buddy zelf
 * verversen om te merken dat de hulpvragen klaar waren.
 *
 * Op de status en niet op "de lijst is nog leeg". Bij opnieuw zoeken staat er
 * al een lijst, en dan werd de vervanging pas zichtbaar na handmatig verversen.
 * De tijdslimiet vangt een verwerking die zonder bericht wegvalt.
 *
 * En dan de wissel zelf. De statusomslag naar EXTRACTED komt bijna altijd nét ná
 * de laatste uitvraag: die kwam nog leeg terug, waarna de herhaling stopte omdat
 * er niets meer liep. Het scherm bleef dan op "geen hulpvragen gevonden" staan
 * terwijl ze in de database stonden. Vandaar één gerichte uitvraag op het moment
 * dat het zoeken klaar is.
 */
export function useHulpvragen(id: string, status?: string) {
  const qc = useQueryClient();
  const tempo = useTempo();
  const bezig = status === 'EXTRACTING' || status === 'CONFIRMED';

  useEffect(() => {
    if (status === 'EXTRACTED') qc.invalidateQueries({ queryKey: gesprekKeys.keywords(id) });
  }, [status, id, qc]);

  return useQuery({
    queryKey: gesprekKeys.keywords(id),
    queryFn: () => api.get<HulpvraagOverzicht>(`/meetings/${id}/hulpvragen`),
    refetchInterval: () => (bezig ? tempo(3000) : false),
  });
}

export function useZoekHulpvragen(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/meetings/${id}/hulpvragen/zoek`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gesprekKeys.detail(id) });
      qc.invalidateQueries({ queryKey: gesprekKeys.alle });
    },
  });
}

function useHulpvraagMutatie<T>(id: string, fn: (invoer: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gesprekKeys.keywords(id) });
      qc.invalidateQueries({ queryKey: gesprekKeys.detail(id) });
      qc.invalidateQueries({ queryKey: gesprekKeys.alle });
    },
  });
}

export const useVoegHulpvraagToe = (id: string) =>
  useHulpvraagMutatie(id, (invoer: { onderwerp: string; deelonderwerp: string; vraag: string }) =>
    api.post(`/meetings/${id}/hulpvragen`, invoer),
  );

/** Een gevonden hulpvraag bijstellen. Alle drie de velden mogen, want een
 *  verkeerd gehoord woord kan in elk ervan staan. */
export const useWerkHulpvraagBij = (id: string) =>
  useHulpvraagMutatie(
    id,
    ({
      hulpvraagId,
      ...velden
    }: {
      hulpvraagId: string;
      onderwerp: string;
      deelonderwerp: string;
      vraag: string;
    }) => api.patch(`/meetings/${id}/hulpvragen/${hulpvraagId}`, velden),
  );

export const useVerwijderHulpvraag = (id: string) =>
  useHulpvraagMutatie(id, (hulpvraagId: string) =>
    api.del(`/meetings/${id}/hulpvragen/${hulpvraagId}`),
  );

export const useStelHulpvragenVast = (id: string) =>
  useHulpvraagMutatie(id, (_leeg: void) =>
    api.post<{ aantal: number }>(`/meetings/${id}/hulpvragen/vaststellen`, {}),
  );
